import React, { Fragment, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Alert from '../../../../../../../components/global/Alert';
import FormGroup from '../../../../../../../components/global/FormGroup';
import { getErrorString } from '../../../../../../../util/normalize-errors';
import Button from '../../../../../../../components/global/Button';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import ImageUpload from '../../../../../../../components/global/ImageUpload';
import UploadIcon from '../../../../../../../components/icons/UploadIcon';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import usePrintAssetForm from './hooks/usePrintAssetForm';
import usePricingCategories from '../../../../../../../components/global/hooks/usePricingCategories';
import Label from '../../../../../../../components/global/Label';
import MultiSelect from '../../../../../../../components/global/MultiSelect';
import {
  PRINT_ASSET_TYPES,
  PRINT_ASSET_SOURCES,
} from '../../../../../../../constants/print-assets';
import { capitalize } from '../../../../../../../util/text';
import JerseyColorSelector from '../../../../../../../components/global/JerseyColorSelector';

const propTypes = {
  printAsset: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.oneOf(Object.values(PRINT_ASSET_TYPES)),
    source: PropTypes.oneOf(Object.values(PRINT_ASSET_SOURCES)),
  }),
  defaultPrintAssetType: PropTypes.oneOf(Object.values(PRINT_ASSET_TYPES)),
  defaultPricingCategoryIds: PropTypes.array,
  defaultAssetImage: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
    original_filename: PropTypes.string,
  }),
  onPrintAssetCreate: PropTypes.func,
  onPrintAssetUpdate: PropTypes.func,
  onRequestClose: PropTypes.func,
};

const PrintAssetForm = ({
  printAsset,
  defaultPrintAssetType,
  defaultPricingCategoryIds,
  defaultAssetImage,
  onPrintAssetCreate,
  onPrintAssetUpdate,
  onRequestClose,
}) => {
  const { printAssets, printAssetCreate, printAssetUpdate } = usePrintAssetForm(
    {
      printAssetType: printAsset?.type ?? defaultPrintAssetType,
    },
  );
  const { pricingCategories } = usePricingCategories();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [workingPrintAsset, setWorkingPrintAsset] = useState(printAsset);
  const getPrintAssetData = printAssetRecord => ({
    name: printAssetRecord?.name ?? '',
    asset_image: printAssetRecord?.asset_image
      ? { ...printAssetRecord.asset_image, is_new: false }
      : undefined,
    type: printAssetRecord?.type ?? defaultPrintAssetType,
  });
  const getPrintAssetCategories = printAssetRecord =>
    (printAssetRecord?.pricing_categories?.records ?? []).map(
      ({ pricing_category: category }) => ({
        label: category.name,
        value: category.id,
        pricing_category: category,
      }),
    );
  const [selectedPricingCategories, setSelectedPricingCategories] = useState(
    getPrintAssetCategories(printAsset),
  );
  const getPrintAssetColors = printAssetRecord =>
    printAssetRecord?.jersey_colors?.records ?? [];

  const [selectedColors, setSelectedColors] = useState(
    getPrintAssetColors(printAsset),
  );
  const [printAssetData, setPrintAssetData] = useState({
    ...getPrintAssetData(printAsset),
    ...(!printAsset?.asset_image && defaultAssetImage
      ? {
          name: (defaultAssetImage.original_filename ?? '').replace(
            /\.[^/.]+$/,
            '',
          ),
          asset_image: { ...defaultAssetImage, is_new: false },
        }
      : {}),
  });

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      setPrintAssetData({
        ...printAssetData,
        asset_image: { ...assetImage, is_new: true },
      });
    },
  });

  useEffect(() => {
    if (
      defaultPricingCategoryIds?.length > 0 &&
      pricingCategories?.length > 0
    ) {
      setSelectedPricingCategories(
        pricingCategories
          .filter(({ id }) => defaultPricingCategoryIds.includes(id))
          .map(category => ({
            label: category.name,
            value: category.id,
            pricing_category: category,
          })),
      );
    }
  }, [defaultPricingCategoryIds, pricingCategories]);

  const printAssetTypeLabel = useMemo(() => {
    if (printAssetData?.type === PRINT_ASSET_TYPES.BACKGROUND) {
      return 'backdrop';
    }

    if (printAssetData?.type === PRINT_ASSET_TYPES.FOREGROUND) {
      return 'foreground';
    }

    if (printAssetData?.type === PRINT_ASSET_TYPES.LOGO) {
      return 'logo';
    }

    return '';
  }, [printAssetData?.type]);

  const onInputChange = event => {
    const { target } = event;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const { name } = target;

    setPrintAssetData({
      ...printAssetData,
      [name]: value,
    });
  };

  const createPrintAsset = async data => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const {
        data: {
          printAssetCreate: { print_asset: createdPrintAsset },
        },
      } = await printAssetCreate({
        ...data,
        source: PRINT_ASSET_SOURCES.USER,
        type: defaultPrintAssetType,
      });

      onPrintAssetCreate?.(createdPrintAsset);
      if (onRequestClose) {
        onRequestClose();
      } else {
        setSubmitting(false);
      }
    } catch (e) {
      setError(getErrorString(e));
      setSubmitting(false);
    }
  };

  const updatePrintAsset = async data => {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const {
        data: {
          printAssetUpdate: { print_asset: updatedPrintAsset },
        },
      } = await printAssetUpdate({
        print_asset_id: printAsset.id,
        ...data,
      });

      setSuccess(true);
      onPrintAssetUpdate?.(updatedPrintAsset);
      setPrintAssetData(getPrintAssetData(updatedPrintAsset));
      setWorkingPrintAsset(updatedPrintAsset);
    } catch (e) {
      setError(getErrorString(e));
    } finally {
      setSubmitting(false);
    }
  };

  const validateForm = () => {
    setError('');

    if (!printAssetData.name) {
      setError(`Please add a name to your ${printAssetTypeLabel}`);
      return false;
    }

    // Check if there's a template with the same name already
    // said template must have a different id, in case of editing.
    if (
      (printAssets ?? []).find(
        asset =>
          asset.name === printAssetData.name &&
          asset.id !== workingPrintAsset?.id,
      )
    ) {
      setError(
        `Please use a unique name for each ${printAssetTypeLabel} style.`,
      );
      return false;
    }

    if (!printAssetData.asset_image) {
      setError(`Please add a design for your ${printAssetTypeLabel}`);
      return false;
    }

    return true;
  };

  const onFormSubmit = event => {
    event.preventDefault();
    event.stopPropagation();

    if (!validateForm()) {
      return;
    }

    const { asset_image: assetImage } = printAssetData ?? {};

    const currentPricingCategories =
      workingPrintAsset?.pricing_categories?.records ?? [];

    const currentJerseyColors = workingPrintAsset?.jersey_colors?.records ?? [];

    const data = {
      name: printAssetData.name,
      asset_image: assetImage
        ? {
            asset_image_id: assetImage.is_new ? assetImage.id : undefined,
            existing_asset_image_id: !assetImage.is_new
              ? assetImage.id
              : undefined,
          }
        : null,
      pricing_categories: {
        create_print_asset_pricing_categories:
          selectedPricingCategories.length > 0
            ? selectedPricingCategories
                .filter(
                  ({ pricing_category: category }) =>
                    !currentPricingCategories.find(
                      ({ pricing_category: currentCategory }) =>
                        category.id === currentCategory.id,
                    ),
                )
                .map(({ pricing_category: category }) => ({
                  pricing_category_id: category.id,
                }))
            : [],
        delete_print_asset_pricing_category_ids:
          currentPricingCategories.length > 0
            ? currentPricingCategories
                .filter(
                  ({ pricing_category: currentCategory }) =>
                    !selectedPricingCategories.find(
                      ({ pricing_category: category }) =>
                        category.id === currentCategory.id,
                    ),
                )
                .map(({ id }) => id)
            : [],
      },
      jersey_colors: {
        create_print_asset_jersey_colors: selectedColors.filter(
          jerseyColor =>
            !jerseyColor?.id &&
            !currentJerseyColors.find(
              currentJerseyColor =>
                currentJerseyColor.jersey_color === jerseyColor.jersey_color,
            ),
        ),
        delete_print_asset_jersey_color_ids: currentJerseyColors
          .filter(
            currentJerseyColor =>
              !selectedColors.find(
                jerseyColor =>
                  currentJerseyColor.jersey_color === jerseyColor.jersey_color,
              ),
          )
          .map(({ id }) => id),
      },
    };

    if (workingPrintAsset) {
      updatePrintAsset(data);
    } else {
      createPrintAsset(data);
    }
  };

  return (
    <Fragment>
      <h4>
        {workingPrintAsset
          ? `Editing asset: ${workingPrintAsset.name}`
          : `Create new ${printAssetTypeLabel}`}
      </h4>

      <form method="post" className="" onSubmit={onFormSubmit}>
        {error && !Array.isArray(error) && <Alert type="danger">{error}</Alert>}

        {success && (
          <Alert type="success" dismissible>
            {capitalize(printAssetTypeLabel)} updated successfully!
          </Alert>
        )}

        <FormGroup
          id="print-asset-name"
          name="name"
          label={`${capitalize(printAssetTypeLabel)} Name`}
          placeholder={`Name your ${printAssetTypeLabel}...`}
          labelSrOnly
          required
          value={printAssetData.name}
          onChange={onInputChange}
        />

        {pricingCategories.length > 0 && (
          <div className="form-group">
            <Label id="category_id" type="expanded">
              What type of shoot is this {printAssetTypeLabel} for?
            </Label>
            <MultiSelect
              id="pricing_category_ids"
              label={`What type of shoot is this ${printAssetTypeLabel} for?`}
              labelSrOnly
              placeholder="Select categories..."
              isMulti
              isLoading={false}
              className="mb-0"
              noOptionsMessage={() => 'No categories found.'}
              value={selectedPricingCategories}
              options={pricingCategories.map(category => ({
                label: category.name,
                value: category.id,
                pricing_category: category,
              }))}
              onChange={value => {
                setSelectedPricingCategories(value);
              }}
              filterOption={(filterItem, search) => {
                const { label } = filterItem;
                return label.toLowerCase().includes(search.toLowerCase());
              }}
            />
          </div>
        )}

        {printAssetData?.type === PRINT_ASSET_TYPES.BACKGROUND && (
          <JerseyColorSelector
            className="mb-2"
            onColorToggle={color => {
              const isSelected = !!selectedColors.find(
                ({ jersey_color: jerseyColor }) =>
                  jerseyColor === color.toUpperCase(),
              );

              if (isSelected) {
                setSelectedColors(prev =>
                  prev.filter(
                    ({ jersey_color: jerseyColor }) => jerseyColor !== color,
                  ),
                );
              } else {
                setSelectedColors([
                  ...selectedColors,
                  { jersey_color: color.toUpperCase() },
                ]);
              }
            }}
            isColorSelected={color =>
              !!selectedColors.find(
                ({ jersey_color: jerseyColor }) =>
                  jerseyColor === color.toUpperCase(),
              )
            }
            tip={
              <p className="text-center mb-0">
                Color tags placed on backgrounds
                <br />
                allow you to dynamically present
                <br />
                only the backgrounds that match each
                <br />
                customer’s jersey color tag, eg if
                <br />
                you upload a red background and want
                <br />
                it to be presented to customers with a
                <br />
                yellow jersey, apply the yellow tag
                <br />
                here. No tags will default to presenting
                <br />
                the background choice to all.
              </p>
            }
          />
        )}

        <div className="row">
          <div className="col-12">
            <div className="specialty-print-form-preview">
              <ImageUpload
                id="specialty-print-preview-upload"
                buttonText={
                  <>
                    <UploadIcon color="#fff" size={16} className="mr-1" />{' '}
                    Upload a design
                  </>
                }
                loading={uploading}
                disabled={uploading}
                image={
                  printAssetData.asset_image
                    ? {
                        id: printAssetData.asset_image.id,
                        thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(
                          size => ({
                            size_code: size,
                            url: printAssetData.asset_image.url,
                            width: 0,
                            height: 0,
                          }),
                        ),
                      }
                    : null
                }
                onInputChange={event => {
                  const [file] = event.target.files;
                  onUpload(file);
                }}
                onImageDismiss={() => {
                  onUploadReset();
                  setPrintAssetData({
                    ...printAssetData,
                    asset_image: null,
                  });
                }}
                customFileInputAccept={
                  (printAsset?.type ?? defaultPrintAssetType) ===
                  PRINT_ASSET_TYPES.FOREGROUND
                    ? 'image/x-png, image/png, .png'
                    : undefined
                }
              />
            </div>
          </div>
        </div>

        <div className="mt-4 d-flex">
          <Button
            type="submit"
            className="mx-auto"
            disabled={
              submitting || !printAssetData.name || !printAssetData.asset_image
            }
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Fragment>
  );
};

PrintAssetForm.propTypes = propTypes;

export default PrintAssetForm;
